import { useQueryParamHelper } from '@shared/core/queryString';
import React, { useCallback, useEffect, useRef, useState } from 'react';
import { LinkV2, TextV2 } from '@withjoy/joykit';
import { StyledStatusBanner } from './StatusBanner.styles';
import { useHistory, useLocation } from '@react-router';
import { useFeatureValue } from '@shared/core/featureFlags';
import { useEventInfo } from '@shared/utils/eventInfo';
import { hasFirebaseDocument } from '@shared/utils/eventInfo/firebaseDocument';
import { sessionHeaderContext } from '@shared/core';
import { getConfig } from '@config';
import { SUCCESS_HOTEL_RECOMMENDATION_BANNER_KEY } from '@shared/constants/bannerKeys';
import { StatusBannerTelemetryProvider, useStatusBannerTelemetry } from './StatusBannerTelemetry';
import { useRecordGuestPreferenceByGuestSessionMutation } from './RecordGuestPreferenceByGuestSession.graphql.generated';
import { addError } from '@shared/utils/logger';

const isDev = getConfig().environment === 'dev';

type BannerPayload = {
  bannerKey: string;
  bannerText: string;
  closeBannerCTA?: string;
  yesBannerCTA?: string;
  noBannerCTA?: string;
}[];

const isValidBanner = (payload: unknown): payload is BannerPayload[number] => {
  if (!payload) {
    return false;
  }

  if (!Object.hasOwn(payload, 'bannerKey')) {
    return false;
  }

  if (!Object.hasOwn(payload, 'bannerText')) {
    return false;
  }

  return true;
};

const isValidBannerData = (payload: unknown): payload is BannerPayload => {
  if (!payload) {
    return false;
  }

  if (!Array.isArray(payload)) {
    return false;
  }

  // If any objects don't have required fields, we are invalid.
  if ((payload as BannerPayload).some(banner => !isValidBanner(banner))) {
    return false;
  }

  return true;
};

export const useGetStatusBannerData = () => {
  const params = useQueryParamHelper();
  const bannerKey = params.getValue('bannerKey');
  const { value: bannerEnabled, payload: bannerPayload } = useFeatureValue('websiteBannerMapping', { skip: !bannerKey });

  if (bannerEnabled !== 'on' || !isValidBannerData(bannerPayload)) {
    return null;
  }

  return bannerPayload.find(banner => banner.bannerKey === bannerKey) ?? null;
};

const useSetBannerState = () => {
  const location = useLocation();
  const history = useHistory();

  const setBannerState = useCallback(
    (newState: string | undefined) => {
      const query = new URLSearchParams(location.search);
      if (newState && newState !== '') {
        query.set('bannerKey', newState);
      } else {
        query.delete('bannerKey');
      }
      const newQuery = query.toString();
      history.replace({ search: newQuery });
    },
    [history, location],
  );

  return { setBannerState };
};

export const StatusBanner = () => {
  const params = useQueryParamHelper();
  const guestId = params.getValue('guestId');
  const [isUpdateInviteePropertyLoading, setIsUpdateInviteePropertyLoading] = useState<boolean>(false);
  const bannerData = useGetStatusBannerData();
  const [isOpen, setIsOpen] = useState(!!bannerData);
  const hasCheckedData = useRef(false);
  const { setBannerState } = useSetBannerState();
  const { eventInfo } = useEventInfo();
  // The legacy REST profile is keyed by the Firebase document. Firebase-free events (new-stack weddings,
  // Pro events) carry a truthy sentinel id instead, so this asks about the document, not the stack.
  const hasLegacyDocument = hasFirebaseDocument(eventInfo?.eventFirebaseId);
  const { trackError, answerClicked, pageViewed, answerNotAccepted } = useStatusBannerTelemetry();
  const [recordGuestPreference] = useRecordGuestPreferenceByGuestSessionMutation();

  const updateInviteeProperty = useCallback(
    async (value: string) => {
      setIsUpdateInviteePropertyLoading(true);
      try {
        // An event without a Firebase document has no profile for the legacy PUT to write, and no
        // `guestId`. The guest is the session's own instead: the session header is the whole authorization,
        // and it is read here, per request, never captured at render (see `sessionHeaderContext`).
        if (!hasLegacyDocument) {
          const eventId = eventInfo?.eventId;
          if (!eventId) {
            throw new Error('needHotelRecommendations mutation skipped: no eventId');
          }
          const { data } = await recordGuestPreference({
            variables: { input: { eventId, preferenceKey: 'needHotelRecommendations', value: value === 'Yes' } },
            context: sessionHeaderContext(),
          });
          const result = data?.recordGuestPreferenceByGuestSession;
          // A response without a payload is a lost answer, not the server's "not stored" — report it
          // as a failure rather than filing it as `accepted: false`.
          if (!result) {
            throw new Error('needHotelRecommendations mutation resolved without a result');
          }
          // The server declining to store the answer is not something the guest can act on: the
          // banner closes exactly as it does after a stored answer, and only telemetry records it.
          if (!result.accepted) {
            answerNotAccepted(value);
          }
          return;
        }

        const baseUrl = isDev ? 'https://dev.withjoy.com' : 'https://withjoy.com';
        // Both segments are encoded: `guestId` comes straight from the URL, and an unencoded `../` or `?` in it
        // would retarget the request. `canRecordHotelAnswer` guarantees both are present for the hotel banner.
        const firebaseIdSegment = encodeURIComponent(eventInfo?.eventFirebaseId ?? '');
        const guestIdSegment = encodeURIComponent(String(guestId ?? ''));
        const response = await fetch(`${baseUrl}/services/api/events/${firebaseIdSegment}/invitees/${guestIdSegment}/profile/needHotelRecommendations`, {
          method: 'PUT',
          headers: { 'content-type': 'application/json' },
          body: JSON.stringify({ propertyValue: value }),
        });
        // Unlike axios, `fetch` resolves (doesn't reject) on a non-2xx HTTP status. Throw
        // explicitly so the catch below still reports server failures instead of silently
        // treating them as success.
        if (!response.ok) {
          throw new Error(`needHotelRecommendations PUT failed: ${response.status}`);
        }
      } catch (e) {
        trackError(e);
      } finally {
        setIsUpdateInviteePropertyLoading(false);
      }
    },
    [hasLegacyDocument, eventInfo?.eventId, eventInfo?.eventFirebaseId, guestId, recordGuestPreference, answerNotAccepted, trackError],
  );

  useEffect(() => {
    if (bannerData && !hasCheckedData.current) {
      setIsOpen(true);
      hasCheckedData.current = true;
      if (bannerData.bannerKey === SUCCESS_HOTEL_RECOMMENDATION_BANNER_KEY) {
        pageViewed();
      }
    }
  }, [bannerData, pageViewed]);

  const closeBanner = () => {
    setIsOpen(false);
    setBannerState('');
  };

  const answerAs = async (answerValue: string) => {
    try {
      setIsOpen(false);
      setBannerState('');
      await updateInviteeProperty(answerValue);
      answerClicked(answerValue);
    } catch (error) {
      addError(error, { context: 'StatusBanner', operation: 'answerAs' });
    }
  };

  // The hotel banner only renders when its answer has somewhere to go: a `guestId` alongside a Firebase
  // document, otherwise the event id (the session supplies the guest).
  const canRecordHotelAnswer = hasLegacyDocument ? !!guestId : !!eventInfo?.eventId;

  if (!isOpen || !bannerData || isUpdateInviteePropertyLoading || (bannerData.bannerKey === SUCCESS_HOTEL_RECOMMENDATION_BANNER_KEY && !canRecordHotelAnswer)) {
    return null;
  }

  return (
    <StatusBannerTelemetryProvider>
      <StyledStatusBanner playConfetti={true}>
        <TextV2 typographyVariant="button1">{bannerData.bannerText}</TextV2>
        {!!bannerData.closeBannerCTA && (
          <LinkV2
            color="black"
            onClick={closeBanner}
            typographyVariant="body1"
          >
            {bannerData.closeBannerCTA}
          </LinkV2>
        )}
        {!!bannerData.yesBannerCTA && (
          <LinkV2
            color="black"
            onClick={() => answerAs('Yes')}
            typographyVariant="body1"
          >
            {bannerData.yesBannerCTA}
          </LinkV2>
        )}
        {!!bannerData.noBannerCTA && (
          <LinkV2
            color="black"
            onClick={() => answerAs('No')}
            typographyVariant="body1"
          >
            {bannerData.noBannerCTA}
          </LinkV2>
        )}
      </StyledStatusBanner>
    </StatusBannerTelemetryProvider>
  );
};
